import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Home, ArrowLeft, TrendingUp, Hammer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertiesList({ user }) {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await axiosInstance.get(`${API}/properties`, {
        withCredentials: true
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error('Erreur de chargement des biens');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Tableau de bord
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                MES <span className="text-cyan-400">BIENS</span>
              </h1>
              <div className="w-32"></div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12">
          {/* Welcome Message */}
          <div className="glass-panel p-6 rounded-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 p-1 shrink-0">
                <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-3xl">
                  🏠
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold font-['Orbitron'] text-cyan-400 mb-2">
                  Salut {user?.name?.split(' ')[0]} !
                </h2>
                <p className="text-slate-300 leading-relaxed">
                  {properties.length === 0 ? (
                    <>
                      <strong>Vous n'avez pas encore de biens enregistrés.</strong> Commençons par créer votre premier investissement immobilier ! 
                      Enregistrez les infos, ajoutez quelques photos, et vous pourrez suivre toutes vos dépenses au fil de l'eau. 
                      Bon investissement ! 💫
                    </>
                  ) : (
                    <>
                      <strong>Vous avez {properties.length} bien{properties.length > 1 ? 's' : ''} dans votre portefeuille.</strong> Continuez de suivre 
                      vos dépenses et simulations pour maximiser votre rentabilité. Votre succès immobilier est en marche ! 🌟
                    </>
                  )}
                </p>
                <Button
                  onClick={() => navigate('/properties/new')}
                  data-testid="create-property-button"
                  className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {properties.length === 0 ? 'Créer mon premier bien' : 'Ajouter un bien'}
                </Button>
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          {properties.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <div
                  key={property.property_id}
                  data-testid={`property-card-${property.property_id}`}
                  className="glass-panel rounded-lg overflow-hidden cursor-pointer hover:border-cyan-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.2)]"
                  onClick={() => navigate(`/properties/${property.property_id}`)}
                >
                  {/* Photo */}
                  <div className="relative h-48 bg-gradient-to-br from-cyan-900/20 to-purple-900/20 overflow-hidden">
                    {property.photo_urls && property.photo_urls.length > 0 ? (
                      <img
                        src={property.photo_urls[0]}
                        alt={property.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="h-16 w-16 text-cyan-400/30" />
                      </div>
                    )}
                    {/* Photo Count Badge */}
                    {property.photo_urls && property.photo_urls.length > 1 && (
                      <div className="absolute top-3 right-3 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full text-xs text-white">
                        📸 {property.photo_urls.length}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold font-['Rajdhani'] text-white mb-2">
                      {property.name}
                    </h3>
                    
                    <p className="text-sm text-slate-400 mb-4 line-clamp-1">
                      📍 {property.address}
                    </p>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="text-sm">
                        <div className="text-slate-500">Surface</div>
                        <div className="text-white font-semibold">{property.surface_m2} m²</div>
                      </div>
                      <div className="text-sm">
                        <div className="text-slate-500">Pièces</div>
                        <div className="text-white font-semibold">{property.nb_rooms}P</div>
                      </div>
                    </div>

                    {/* Investment Summary */}
                    <div className="pt-4 border-t border-white/10 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400 flex items-center gap-1">
                          <Hammer className="h-3 w-3" />
                          Travaux
                        </span>
                        <span className="text-cyan-400 font-semibold">
                          {property.total_travaux?.toLocaleString() || 0} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400 flex items-center gap-1">
                          🛋️ Aménagement
                        </span>
                        <span className="text-purple-400 font-semibold">
                          {property.total_amenagement?.toLocaleString() || 0} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-white/5">
                        <span className="text-slate-300 font-bold">Total investi</span>
                        <span className="text-white font-bold">
                          {property.total_invested?.toLocaleString() || 0} €
                        </span>
                      </div>
                    </div>

                    {/* Expense Count Badge */}
                    {property.expense_count > 0 && (
                      <div className="mt-4 text-xs text-center text-slate-400">
                        💰 {property.expense_count} dépense{property.expense_count > 1 ? 's' : ''} enregistrée{property.expense_count > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
